import { useCallback } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, ROUTES } from '@/shared/lib/constants';
import { glossaryKeys } from '@/shared/lib/query/queryKeys';

import { deleteGlossaryCategoryApi, type DeleteGlossaryCategoryRequest } from '@/features/glossary/api';

export const useDeleteGlossaryCategory = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { termCategory } = useParams<{ termCategory?: string }>();

  const mutation = useMutation({
    mutationFn: (request: DeleteGlossaryCategoryRequest) => deleteGlossaryCategoryApi(request),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: glossaryKeys.glossaryTree }),
        queryClient.invalidateQueries({ queryKey: glossaryKeys.categoriesAll() }),
        queryClient.invalidateQueries({ queryKey: glossaryKeys.categories(3000) }),
      ]);

      navigate(ROUTES.GLOSSARY, { replace: true });
      queryClient.removeQueries({ queryKey: glossaryKeys.categoryInfo(termCategory ?? '') });
      queryClient.removeQueries({ queryKey: glossaryKeys.category(termCategory ?? '') });

      notification.success({
        message: NOTIFICATION_MESSAGES.GLOSSARY_CATEGORY_DELETE_SUCCESS,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleDelete = useCallback(
    (glossaryId: string, options?: { recursive?: boolean; hardDelete?: boolean }) => {
      return mutation.mutateAsync({
        glossaryId,
        recursive: options?.recursive ?? true,
        hardDelete: options?.hardDelete ?? true,
      });
    },
    [mutation]
  );

  return {
    ...mutation,
    handleDelete,
  };
};
